import { useEffect, useRef } from 'react';

import { firebase } from 'redux/firebase';
import { useTypedSelector } from 'redux/reducers';
import { notify } from '../HotToastNotifications';
import { Message, Chat } from 'components/Chatter/types';
import { getNotificationAudio, NotificationAudio } from '../notificationSound';
import {
  getSelectedChatFromStorage,
  buildMessagePreview,
  buildDealLink
} from './helpers';
import { customerAsPrimaryContact } from 'components/Chatter/helpers';
import { shouldPlayNotificationAudio } from './audioSync';

/**
 * Watches chatter unread counters for the current user and raises a toast when a new
 * unread message arrives. Limited to a single Firestore listener scoped by user.
 */
export const ChatterNotificationsListener = () => {
  const authToken = useTypedSelector((s) => s.user.auth?.token);
  const userId = useTypedSelector((s) => s.user.user?.Id);
  const hasInitialised = useRef(false);
  const notificationAudioRef = useRef<NotificationAudio | null>(null);
  const deal = useTypedSelector((s) => s.dealSummary.deal);

  const isLandingPage = location.pathname === '/landingpage';

  let primaryContactUserId;
  if (isLandingPage) {
    const primaryContact = customerAsPrimaryContact({
      deal,
      userId
    });

    primaryContactUserId = primaryContact?.userId;
  }

  const chatterUserId = primaryContactUserId ?? userId;

  useEffect(() => {
    if (!authToken || !chatterUserId) {
      hasInitialised.current = false;
      return;
    }

    const db = firebase.firestore();
    const collectionRef = db
      .collection(`/chatterUnreads/${chatterUserId}/chats`)
      .where('count', '>', 0);

    const unsubscribe = collectionRef.onSnapshot((snapshot) => {
      // prevent showing latest unread toast everytime the listener mounts initially (page refresh / login etc)
      if (!hasInitialised.current) {
        hasInitialised.current = true;
        return;
      }

      const changeTasks = snapshot.docChanges().map(async (change) => {
        const chatId = change.doc.id;
        const chat = change.doc.data() as Chat;

        // prevent notification from popping up when we clear the unreadCount
        if (change.type === 'removed') return;

        const dealId = chat?.dealId;
        if (!dealId) return;

        try {
          const chatDoc = await db
            .doc(`/chatter/${dealId}/chats/${chatId}`)
            .get();
          if (!chatDoc.exists) return;

          const chat = chatDoc.data() as Record<string, any>;
          const latestMessage = (chat?.latestMessage ?? null) as Message | null;

          const isMessageFromCurrentUser =
            latestMessage?.senderId === chatterUserId;

          if (isMessageFromCurrentUser) return;

          const currentChat = getSelectedChatFromStorage();

          // dont show notification for currently selected chat
          if (currentChat?.chatId === chatId && currentChat.dealId === dealId) {
            return;
          }

          const message = buildMessagePreview(latestMessage);
          const toastId = `chatter-${dealId}-${chatId}`;
          const link =
            (!isLandingPage &&
              buildDealLink({
                dealId,
                dealName: chat.dealName,
                dealDefId: chat.dealDefId,
                dealStepDefId: chat.dealStepDefId
              })) ||
            '';

          const latestMessageId = latestMessage?.id;

          if (shouldPlayNotificationAudio(latestMessageId)) {
            notificationAudioRef.current?.play();
          }

          notify.chatter(message, {
            title: `Deal #${dealId}: ${latestMessage?.senderTitle} sent you a message!`,
            link,
            duration: Infinity,
            toastId
          });
        } catch (error) {
          console.error('Failed to build chatter notification', error);
        }
      });

      if (changeTasks.length) {
        Promise.allSettled(changeTasks).catch((error) =>
          console.error('Error processing chatter notifications', error)
        );
      }
    });

    return () => {
      hasInitialised.current = false;
      unsubscribe();
    };
  }, [authToken, chatterUserId]);

  // Plays notification sound upon receiving a notification
  useEffect(() => {
    let isMounted = true;

    const loadNotificationAudio = async () => {
      if (!authToken || !userId) return;

      try {
        const audio = await getNotificationAudio(userId);
        if (isMounted) notificationAudioRef.current = audio;
      } catch (error) {
        console.warn('Failed to load chatter notification audio', error);
      }
    };

    loadNotificationAudio();

    return () => {
      isMounted = false;
    };
  }, [userId, authToken]);

  return null;
};

export default ChatterNotificationsListener;
